import React, { useMemo } from "react";
import { userSortName } from "../../../api";
import { ID, User } from "../../../types";
import { SearchableSelect } from "./SearchableSelect";
import { FormLabel } from "./FormField";

// Select degli utenti: usa sempre `userSortName` ("Cognome Nome") perché le
// liste utente sono ordinate per cognome, e la ricerca testuale di
// `SearchableSelect` per non dover scorrere elenchi lunghi.

interface UserSelectProps {
  users: User[];
  value: ID | null;
  onChange: (userId: ID | null) => void;
  // Utenti mostrati ma non selezionabili (es. già scelti altrove).
  disabledUserIds?: ID[];
  placeholder?: string;
  // Se valorizzato aggiunge l'opzione che azzera la selezione.
  emptyOptionLabel?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

export const UserSelect: React.FC<UserSelectProps> = ({
  users,
  value,
  onChange,
  disabledUserIds,
  placeholder = "Seleziona utente",
  emptyOptionLabel,
  disabled,
  className,
  id,
  "aria-label": ariaLabel,
}) => {
  const disabledIds = useMemo(() => new Set(disabledUserIds ?? []), [disabledUserIds]);

  const options = useMemo(
    () =>
      users.map((user) => ({
        value: String(user.id),
        label: userSortName(user),
        disabled: disabledIds.has(user.id),
      })),
    [disabledIds, users]
  );

  return (
    <SearchableSelect
      id={id}
      aria-label={ariaLabel}
      options={options}
      value={value != null ? String(value) : ""}
      onChange={(next) => onChange(next === "" ? null : Number(next))}
      placeholder={placeholder}
      emptyOptionLabel={emptyOptionLabel}
      searchPlaceholder="Cerca nome o cognome..."
      noResultsLabel="Nessun utente trovato"
      disabled={disabled}
      className={className}
    />
  );
};

type UserSelectFieldProps = UserSelectProps & {
  label: React.ReactNode;
  labelClassName?: string;
};

// Variante con label sopra, allineata a `SelectField` di FormField.
export const UserSelectField: React.FC<UserSelectFieldProps> = ({
  label,
  labelClassName,
  ...selectProps
}) => (
  <FormLabel label={label} className={labelClassName}>
    <UserSelect {...selectProps} />
  </FormLabel>
);
